import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Calendar, Loader2, Video, Plus, X, RefreshCw, Unlink, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hidden until Google OAuth verification completes — flip to true to re-enable
const CALENDAR_FEATURE_ENABLED = true;

export const CalendarMeetings = () => {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyEvent, setBusyEvent] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/calendar/status`);
      setStatus(data);
      return data;
    } catch (_e) { return null; }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/calendar/events`);
      setEvents(data.events || []);
    } catch (e) {
      if (e.response?.status !== 404) toast.error(e.response?.data?.detail || 'Could not load meetings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!CALENDAR_FEATURE_ENABLED) return;
    (async () => {
      const s = await loadStatus();
      if (s?.connected) loadEvents();
      const params = new URLSearchParams(window.location.search);
      if (params.get('calendar_connected')) {
        toast.success('Calendar connected! Your upcoming meetings will appear below.');
        window.history.replaceState({}, '', window.location.pathname);
      } else if (params.get('calendar_error')) {
        toast.error(`Calendar connection failed: ${params.get('calendar_error')}`);
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
  }, [loadStatus, loadEvents]);

  const connect = async (provider) => {
    try {
      const { data } = await axios.get(`${API}/calendar/oauth/${provider}/url`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start calendar connection');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect your calendar? Scheduled bots for future meetings will be removed.')) return;
    try {
      await axios.delete(`${API}/calendar/disconnect`);
      setEvents([]);
      await loadStatus();
      toast.success('Calendar disconnected');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  const toggleBot = async (ev, optIn) => {
    setBusyEvent(ev.id);
    try {
      const { data } = await axios.post(`${API}/calendar/events/${ev.id}/schedule`, { opt_in: optIn });
      toast.success(data.message);
      await loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Update failed');
    } finally {
      setBusyEvent(null);
    }
  };

  const setAutoJoin = async (val) => {
    try {
      await axios.post(`${API}/calendar/preferences`, { auto_join_external: val });
      setStatus(s => ({ ...s, auto_join_external: val }));
      toast.success(val ? 'Bot will auto-join your external meetings (you can skip any meeting below)' : 'Auto-join off — add the bot per meeting');
    } catch (_e) {
      toast.error('Could not save preference');
    }
  };

  if (!CALENDAR_FEATURE_ENABLED) return null;
  if (!status) return null;
  const anyProvider = status.providers_available?.google || status.providers_available?.microsoft;
  if (!anyProvider) return null;

  return (
    <div className="border border-zinc-200 rounded-lg bg-white" data-testid="calendar-meetings-panel">
      <button
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="calendar-panel-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-zinc-700">
          <Calendar className="w-4 h-4 text-brand-primary" />
          Upcoming Meetings
          {status.connected && <Badge variant="outline" className="text-xs font-normal">{status.email}</Badge>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {!status.connected ? (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500">Connect your calendar and PipeGuide can join your meetings automatically — no more copy-pasting links.</p>
              <div className="flex gap-2">
                {status.providers_available.google && (
                  <Button size="sm" variant="outline" className="rounded-md" onClick={() => connect('google')} data-testid="connect-google-calendar-btn">
                    <Calendar className="w-4 h-4 mr-1.5" /> Connect Google Calendar
                  </Button>
                )}
                {status.providers_available.microsoft ? (
                  <Button size="sm" variant="outline" className="rounded-md" onClick={() => connect('microsoft')} data-testid="connect-outlook-calendar-btn">
                    <Calendar className="w-4 h-4 mr-1.5" /> Connect Outlook
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-md opacity-50" disabled title="Outlook coming soon">
                    <Calendar className="w-4 h-4 mr-1.5" /> Outlook (soon)
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 bg-zinc-50 border border-zinc-100 rounded-md px-3 py-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!status.auto_join_external}
                    onCheckedChange={setAutoJoin}
                    data-testid="auto-join-toggle"
                  />
                  <span className="text-xs text-zinc-600">Auto-join my external meetings</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-zinc-500" onClick={loadEvents} disabled={loading} data-testid="refresh-events-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-zinc-500" onClick={disconnect} data-testid="disconnect-calendar-btn">
                    <Unlink className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {loading && events.length === 0 ? (
                <div className="flex items-center gap-2 text-xs text-zinc-400 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Syncing meetings…</div>
              ) : events.length === 0 ? (
                <p className="text-xs text-zinc-400 py-2" data-testid="no-upcoming-meetings">No upcoming meetings with video links in the next 14 days.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="upcoming-meetings-list">
                  {events.map(ev => (
                    <div key={ev.id} className="flex items-center justify-between gap-3 border border-zinc-100 rounded-md px-3 py-2" data-testid={`meeting-row-${ev.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm text-zinc-800 truncate">{ev.title}</p>
                        <p className="text-xs text-zinc-400">
                          {ev.start_time ? new Date(ev.start_time).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          {ev.platform && <> · {ev.platform.replace(/_/g, ' ')}</>}
                          {!ev.is_external && <> · internal</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!ev.meeting_url ? (
                          <span className="text-xs text-zinc-300">no video link</span>
                        ) : ev.bot_scheduled ? (
                          <>
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs" variant="outline"><Video className="w-3 h-3 mr-1" /> Bot joining</Badge>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-600" disabled={busyEvent === ev.id}
                              onClick={() => toggleBot(ev, false)} data-testid={`skip-bot-btn-${ev.id}`}>
                              {busyEvent === ev.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                              <span className="ml-1 text-xs">Skip</span>
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 rounded-md" disabled={busyEvent === ev.id}
                            onClick={() => toggleBot(ev, true)} data-testid={`add-bot-btn-${ev.id}`}>
                            {busyEvent === ev.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                            <span className="text-xs">Add PipeGuide</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarMeetings;
